import { apiClient } from '@/lib/api/client'
import type {
  EmailTemplate,
  EmailCampaign,
  EmailSend,
  AudienceFilter,
  OverallEmailStats,
} from '../types'

export interface GetTemplatesParams {
  limit?: number
  offset?: number
  category?: string
  isActive?: boolean
  isSystem?: boolean
  search?: string
}

export interface CreateTemplatePayload {
  name: string
  slug: string
  subject: string
  description?: string
  htmlContent: string
  textContent?: string
  category?: string
  variables?: string[]
}

export interface UpdateTemplatePayload extends Partial<CreateTemplatePayload> {
  isActive?: boolean
}

export interface GetCampaignsParams {
  limit?: number
  offset?: number
  status?: string
  search?: string
}

export interface CreateCampaignPayload {
  name: string
  description?: string
  templateId?: number
  subject: string
  htmlContent?: string
  textContent?: string
  audienceFilter?: AudienceFilter
  scheduledAt?: string
}

export interface UpdateCampaignPayload extends Partial<CreateCampaignPayload> {}

export interface GetEmailHistoryParams {
  limit?: number
  offset?: number
  recipientId?: number
  campaignId?: number
  emailType?: string
  status?: string
}

export interface SendBulkEmailPayload {
  artistIds: number[]
  subject: string
  htmlContent: string
  textContent?: string
}

interface PaginatedResponse<T> {
  data: T[]
  count: number
}

export const emailAdminApi = {
  // ==========================================
  // Email Templates
  // ==========================================

  getTemplates: async (params: GetTemplatesParams = {}): Promise<PaginatedResponse<EmailTemplate>> => {
    const response = await apiClient.get('/api/admin/email/templates', { params })
    const count = parseInt(response.headers['count'] || '0')
    return { data: response.data, count }
  },

  getTemplate: async (templateId: number): Promise<EmailTemplate> => {
    const response = await apiClient.get(`/api/admin/email/templates/${templateId}`)
    return response.data
  },

  createTemplate: async (payload: CreateTemplatePayload): Promise<EmailTemplate> => {
    const response = await apiClient.post('/api/admin/email/templates', payload)
    return response.data
  },

  updateTemplate: async (templateId: number, payload: UpdateTemplatePayload): Promise<EmailTemplate> => {
    const response = await apiClient.put(`/api/admin/email/templates/${templateId}`, payload)
    return response.data
  },

  deleteTemplate: async (templateId: number): Promise<void> => {
    await apiClient.delete(`/api/admin/email/templates/${templateId}`)
  },

  // ==========================================
  // Email Campaigns
  // ==========================================

  getCampaigns: async (params: GetCampaignsParams = {}): Promise<PaginatedResponse<EmailCampaign>> => {
    const response = await apiClient.get('/api/admin/email/campaigns', { params })
    const count = parseInt(response.headers['count'] || '0')
    return { data: response.data, count }
  },

  getCampaign: async (campaignId: number): Promise<EmailCampaign> => {
    const response = await apiClient.get(`/api/admin/email/campaigns/${campaignId}`)
    return response.data
  },

  createCampaign: async (payload: CreateCampaignPayload): Promise<EmailCampaign> => {
    const response = await apiClient.post('/api/admin/email/campaigns', payload)
    return response.data
  },

  updateCampaign: async (campaignId: number, payload: UpdateCampaignPayload): Promise<EmailCampaign> => {
    const response = await apiClient.put(`/api/admin/email/campaigns/${campaignId}`, payload)
    return response.data
  },

  deleteCampaign: async (campaignId: number): Promise<void> => {
    await apiClient.delete(`/api/admin/email/campaigns/${campaignId}`)
  },

  sendCampaign: async (campaignId: number): Promise<{ success: boolean; message: string; sentCount: number; totalRecipients: number }> => {
    const response = await apiClient.post(`/api/admin/email/campaigns/${campaignId}/send`)
    return response.data
  },

  // ==========================================
  // Email History & Stats
  // ==========================================

  getEmailHistory: async (params: GetEmailHistoryParams = {}): Promise<PaginatedResponse<EmailSend>> => {
    const response = await apiClient.get('/api/admin/email/history', { params })
    const count = parseInt(response.headers['count'] || '0')
    return { data: response.data, count }
  },

  getEmailStats: async (): Promise<OverallEmailStats> => {
    const response = await apiClient.get('/api/admin/email/stats')
    return response.data
  },

  sendBulkEmail: async (payload: SendBulkEmailPayload): Promise<{ success: boolean; message: string; sentCount: number; skippedCount: number }> => {
    const response = await apiClient.post('/api/admin/email/send-bulk', payload)
    return response.data
  },
}
